import { gql } from '@apollo/client';
import { gql as graphql } from '../../gql/gql';
/*
 ════════════════════════════════
 =========== LAYOUTS ===========
 ════════════════════════════════
 */

export const LAYOUT_POSITION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment LayoutPositionCoreFragment on LayoutPosition {
    __typename
    x
    y
  }
`);

export const LAYOUT_GRID_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment LayoutGridCoreFragment on GridLayoutProperties {
    __typename
    xs
    sm
    md
    lg
    xl
    xxl
  }
`);

export const LAYOUT_CONFIGURATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment LayoutConfigurationCoreFragment on LayoutConfiguration {
    __typename
    column {
      ...LayoutGridCoreFragment
    }
  }
`);

export const PAGE_LAYOUT_CONFIGURATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PageLayoutConfigurationCoreFragment on ComponentLayoutConfiguration {
    __typename
    id
    column {
      ...LayoutGridCoreFragment
    }
    component
    position {
      ...LayoutPositionCoreFragment
    }
    props
  }
`);

/*
 ═════════════════════════════════════════
 =========== MEASUREMENTS ===========
 ═════════════════════════════════════════
 */

export const QUANTITY_CORE_FRAGMENT = gql`
  fragment QuantityCoreFragment on Quantity {
    __typename
    quantity_id
    value
    unit
    code
    system
  }
`;

export const RATIO_CORE_FRAGMENT = gql`
  ${QUANTITY_CORE_FRAGMENT}
  fragment RatioCoreFragment on Ratio {
    __typename
    ratio_id
    numerator {
      ...QuantityCoreFragment
    }
    denominator {
      ...QuantityCoreFragment
    }
  }
`;

/*
 ════════════════════════════════
 ======== DATA TYPES ========
 ════════════════════════════════
 */

export const TAG_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TagCoreFragment on Tags {
    __typename
    tag_id
    tag
    name
    slug
    icon_url
  }
`);

export const ATTRIBUTE_VALUES_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment AttributeValuesCoreFragment on AttributeValues {
    __typename
    value_id
    value
    color
  }
`);

export const ATTRIBUTE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment AttributeCoreFragment on Attributes {
    __typename
    attribute_id
    attribute
    name
    values {
      ...AttributeValuesCoreFragment
    }
  }
`);

export const IDENTIFIER_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment IdentifierCoreFragment on Identifiers {
    __typename
    identifier_id
    type
    value
    use
    system
  }
`);


export const RESOURCE_REFERENCE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ResourceReferenceCoreFragment on References {
    __typename
    reference_id
    type
    display
    reference
  }
`);

export const ICD10_CORE_FRAGMENT = gql`
  fragment ICD10CoreFragment on ICD10 {
    __typename
    icd10_code
    icd11_code
    label
    description
    type
    hex
  }
`;

export const ICD10_CODE_FRAGMENT = gql(`
  fragment ICD10CodeFragment on ICD10 {
    __typename
    icd10_code
    label
  }
`);

/*
 ════════════════════════════════
 ======== TASKS ========
 ════════════════════════════════
 */

export const TASK_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TaskCoreFragment on Tasks {
    __typename
    task_id
    notification_id
    title
    description
    status
    business_status
    status_reason
    label
    progress
    code
    reason
    url
    link
    priority
    do_not_perform
    assignee_function
    assignee_role
    last_modified
    started_at
    completed_at
    created_at
  }
`);

export const TASK_STEP_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TaskStepCoreFragment on TaskSteps {
    __typename
    step_id
    label
    description
    status
    order
    required
    progress
    link
    url
    status_reason
    started_at
  }
`);


/*
 ════════════════════════════════
 ======== FEEDBACK ========
 ════════════════════════════════
 */
export const QUESTIONNAIRE_CORE_FRAGMENT = gql`
  fragment QuestionnaireCoreFragment on Questionnaires {
    __typename
    questionnaire_number
    title
    url
    description
    link_id
    publisher
    publish_date
    status
    subject_type
    purpose
    last_review_date
    version
    created_at
  }
`;

export const QUESTIONNAIRE_ITEM_CORE_FRAGMENT = gql`
  fragment QuestionnaireItemCoreFragment on QuestionnaireItems {
    __typename
    item_id
    link_id
    item_type
    input_type
    response_label
    appearance {
      __typename
      column {
        xs
        sm
        md
        lg
        xl
        xxl
      }
    }
    order
    text
    prefix
    answer_constraint
    disabled_display
    enable_behavior
    max_length
    ready_only
    required
    repeats
    created_at
  }
`;

export const QUESTIONNAIRE_RESPONSE_CORE_FRAGMENT = gql`
  fragment QuestionnaireResponseCoreFragment on QuestionnaireResponses {
    __typename
    response_id
    status
    authored
    completed_at
    created_at
    updated_at
    progress
    questionnaire {
      __typename
      questionnaire_number
    }
  }
`;

export const QUESTIONNAIRE_ANSWERS_CORE_FRAGMENT = gql`
  ${QUANTITY_CORE_FRAGMENT}
  fragment QuestionnaireAnswersCoreFragment on QuestionnaireAnswers {
    __typename
    answer_id
    answer_decimal
    answer_date
    answer_time
    answer_integer
    answer_boolean
    answer_coding
    answer_attachment
    answer_string
    answer_uri
    answer_option {
      __typename
      option_id
      label
    }
    answer_range {
      high {
        ...QuantityCoreFragment
      }
      low {
        ...QuantityCoreFragment
      }
    }
    answer_quantity {
      __typename
      quantity_id
      value
      unit
      code
      system
      created_at
    }
  }
`;

export const QUESTIONNAIRE_ENABLE_WHEN_CORE_FRAGMENT = gql`
  fragment QuestionnaireEnableWhenCoreFragment on QuestionnaireEnableWhen {
    __typename
    enable_id
    link_id
    answer_boolean
    answer_coding
    answer_date
    answer_time
    answer_decimal
    answer_integer
    answer_quantity {
      __typename
      quantity_id
      value
      unit
      system
      code
      created_at
    }
  }
`;

export const QUESTIONNAIRE_ANSWER_OPTIONS_FRAGMENT = gql`
  ${QUANTITY_CORE_FRAGMENT}
  fragment QuestionnaireAnswerOptionsCoreFragment on QuestionnaireAnswerOptions {
    __typename
    option_id
    label
    icon
    initial_selected
    option_coding
    option_date
    option_time
    option_integer
    option_string
    option_boolean
    option_range {
      low {
        ...QuantityCoreFragment
      }
      high {
        ...QuantityCoreFragment
      }
    }
    created_at
  }
`;

export const QUESTIONNAIRE_ABX_STATISTICS_FRAGMENT = gql`
  ${QUANTITY_CORE_FRAGMENT}
  fragment QuestionnaireABXStatisticsFragment on QuestionnaireResponses {
    __typename
    response_id
    questionnaire {
      __typename
      questionnaire_number
    }
    answers {
      answer_id
      question {
        item_id
        link_id
      }
      answer_range {
        range_id
        low {
          ...QuantityCoreFragment
        }
        high {
          ...QuantityCoreFragment
        }
      }
    }

  }
`;

/*
 ═════════════════════════════════════════════════
 =========== MESSAGES/NOTIFICATIONS ===========
 ═════════════════════════════════════════════════
 */

export const NOTIFICATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment NotificationCoreFragment on Notifications {
    __typename
    is_read
    notification_id
    notification_title
    notification_body
    notification_data
    notification_category
    notification_link
    link_label
    notification_priority
    from_cynergy_id
    recipient_cynergy_id
    ref_id
    from
    created_at
    updated_at
  }
`);

export const MESSAGE_CORE_FRAGMENT = graphql(/* GraphQL */`
  ${NOTIFICATION_CORE_FRAGMENT}
  fragment MessageCoreFragment on Messages {
    __typename
    message_id
    cynergy_id
    title
    body
    from_email
    folder
    labels
    is_read
    slug
    is_trashed
    is_starred
    attachment
    created_at
    notification {
      ...NotificationCoreFragment
    }
  }
`);

/*
 ════════════════════════════════
 =========== USER ===========
 ════════════════════════════════
 */

// #DEPRECATED use CYNERGY_ACCOUNT_CORE_FRAGMENT_ALT instead
export const CYNERGY_ACCOUNT_CORE_FRAGMENT = gql`
  fragment CynergyAccountCoreFragment on CynergyUsers {
    __typename
    cynergy_id
    email
    phone
    username
    validated
    is_default
    created_at
    updated_at
  }
`;

export const CYNERGY_ACCOUNT_CORE_FRAGMENT_ALT = graphql(/* GraphQL */`
  fragment CynergyAccountCoreFragmentAlt on CynergyUsers {
    __typename
    cynergy_id
    email
    phone
    username
    validated
    is_default
    created_at
    updated_at
  }
`);

// #DEPRECATED use DEMOGRAPHIC_CORE_FRAGMENT_ALT instead
export const DEMOGRAPHICS_CORE_FRAGMENT = gql`
  fragment DemographicsCoreFragment on Demographics {
    __typename
    cynergy_id
    full_name
    first_name
    last_name
    prefix
    suffix
    middle_name
    age
    deceased
    dob
    ethnicity
    sex
    race
  }
`;

export const DEMOGRAPHICS_CORE_FRAGMENT_ALT = graphql(/* GraphQL */`
  fragment DemographicsCoreFragmentAlt on Demographics {
    __typename
    cynergy_id
    full_name
    first_name
    last_name
    prefix
    suffix
    middle_name
    age
    deceased
    dob
    ethnicity
    marital_status
    sex
    race
  }
`);

// #DEPRECATED use USER_CORE_FRAGMENT_ALT instead
export const USER_CORE_FRAGMENT = gql`
  ${CYNERGY_ACCOUNT_CORE_FRAGMENT}
  ${DEMOGRAPHICS_CORE_FRAGMENT}
  fragment UserCoreFragment on Users {
    __typename
    cynergy_id
    user_id
    username
    first_login
    identity {
      ...CynergyAccountCoreFragment
    }
    demographics {
      ...DemographicsCoreFragment
    }
  }
`;

export const USER_CORE_FRAGMENT_ALT = graphql(/* GraphQL */`
  fragment UserCoreFragmentAlt on Users {
    __typename
    cynergy_id
    user_id
    username
  }
`);

export const PATIENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PatientCoreFragment on Patients {
    __typename
    patient_id
    user {
      __typename
      user_id
    }
  }
`);

export const LOCATION_PATIENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment LocationPatientCoreFragment on LocationPatients {
    __typename
    status
    patient_id
    location_id
    patient {
      patient_id
      user {
        user_id
        demographics {
          cynergy_id
          first_name
          last_name
          full_name
          dob
          age
        }
      }
    }
  }
`);

export const DEPENDENT_CORE_FRAGMENT = gql`
  fragment DependentCoreFragment on Dependents {
    __typename
    patient_id
    parent_patient_id
    parent {
      __typename
      patient_id
    }
    dependent {
      __typename
      patient_id
    }
  }
`;

export const USER_EVENTS_CORE_FRAGMENT = gql`
  fragment UserEventsCoreFragment on UserEventData {
    __typename
    id
    title
    start
    end
    meta {
      __typename
      type
      participants {
        label
        value
      }
      description
      location
    }
    link
  }
`;

export const GROUP_CORE_FRAGMENT = gql`
  fragment GroupCoreFragment on Groups {
    __typename
    group_id
    name
    description
    type
    membership
    active
    code
    created_at
  }
`;

/*
 ════════════════════════════════
 =========== 2FA ===========
 ════════════════════════════════
 */

export const TWILIO_FACTOR_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TwilioFactorCoreFragment on TwilioFactor {
    __typename
    factor_id
    sid
    name
    verified
  }
`);

export const TWO_FACTOR_AUTHENTICATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TwoFactorAuthenticationCoreFragment on TwoFactorAuthentication {
    __typename
    two_factor_id
    cynergy_id
    enabled
    method
    sms_number
    sms_verified
  }
`);

export const TWO_FACTOR_TOTP_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TwoFactorTOTPCoreFragment on TwoFactorTotp {
    __typename
    two_factor_id
    qr_uri
    secret
  }
`);

export const TWO_FACTOR_PUSH_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TwoFactorPushCoreFragment on TwoFactorPush {
    __typename
    two_factor_id
  }
`);

/*
 ═══════════════════════════════════════
 ======== EMPLOYEE/PRACTITIONER ========
 ═══════════════════════════════════════
 */

export const STAFF_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment StaffCoreFragment on Staffs {
    __typename
    staff_id
    created_at
  }
`);

export const STAFF_ASSIGNMENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment StaffAssignmentCoreFragment on StaffAssignments {
    __typename
    assignment_id
    organization_id
    location_id
    staff_id
    title
    status
    hire_date
  }
`);

export const PRACTITIONER_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PractitionerCoreFragment on Practitioners {
    __typename
    practitioner_id
    cynergy_id
    registration_authority_id
    npi
    prefix
    suffix
    created_at
    affiliation_date
  }
`);

export const PRACTITIONER_AFFILIATIONS_FRAGMENT = graphql(/* GraphQL */`
  fragment PractitionerAffiliationsFragment on Practitioners {
    __typename
    practitioner_id
    location_affiliations {
      __typename
      role
    }
  }
`) as any;


export const PRACTITIONER_ASSIGNMENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PractitionerAssignmentCoreFragment on StaffAssignments {
    __typename
    assignment_id
    staff_id
    location_id
    organization_id
    status
    title
    hire_date
    status
  }
`);

export const PRACTITIONER_QUALIFICATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PractitionerQualificationCoreFragment on PractitionerQualifications {
    __typename
    qualification_id
    practitioner_id
    credential
    label
    license
    speciality
    is_verified
    verified_on
    created_at
  }
`);

export const PRACTITIONER_SIGNATURE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PractitionerSignatureCoreFragment on PractitionerSignatures {
    __typename
    signature_id
    signature_format
    url
    when
  }
`);

/*
 ══════════════════════════════════
 ======== QUALIFICATIONS ========
 ══════════════════════════════════
 */

export const TAXONOMIES_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment TaxonomiesCoreFragment on Taxonomies {
    __typename
    taxonomy_id
    license
    code
    primary
    description
    group
    state
  }
`);

/*
 ════════════════════════════════
 ======== SCHEDULES ========
 ════════════════════════════════
 */

export const PERIOD_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PeriodCoreFragment on Periods {
    __typename
    period_id
    start
    end
  }
`);

export const RECURRENCE_PATTERN_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment RecurrencePatternCoreFragment on RecurrencePatterns {
    __typename
    pattern_id
    start_date
    end_date
    description
    timezone
    text
  }
`);

export const RECURRENCE_PATTERN_WEEKLY_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment RecurrencePatternWeeklyCoreFragment on WeeklyRecurrencePatterns {
    __typename
    pattern_id
    interval
    days
    every_day
  }
`);

export const RECURRENCE_PATTERN_MONTHLY_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment RecurrencePatternMonthlyCoreFragment on MonthlyRecurrencePatterns {
    __typename
    pattern_id
    interval
    day_of_month
    day_of_week
    week_of_month
  }
`);

/*
 ════════════════════════════
 ======== EVENTS ========
 ════════════════════════════
 */

export const EVENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment EventCoreFragment on Events {
    __typename
    event_id
    name
    description
    type
    event_date
    all_day_event
    start_time
    end_time
    repeats
  }
`);

export const EVENT_SERIES_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment EventSeriesCoreFragment on EventSeries {
    __typename
    series_id
    start_time
    end_time
  }
`);

/*
 ════════════════════════════════
 ======== ENCOUNTERS ========
 ════════════════════════════════
 */

export const ENCOUNTER_CORE_FRAGMENT = gql`
  fragment EncounterCoreFragment on Encounters {
    __typename
    encounter_id
    status
    class
    priority
    service_type
    subject_status
    planned_start_date
    planned_end_date
    diet_preference
    special_arrangement
    special_courtesy
    created_at
    deleted_at
  }
`;

export const ENCOUNTER_REASONS_CORE_FRAGMENT = gql`
  fragment EncounterReasonsCoreFragment on EncounterReasons {
    __typename
    reason_code
    use
    created_at
  }
`;

export const ENCOUNTER_PARTICIPANTS_CORE_FRAGMENT = gql`
  fragment EncounterParticipantsCoreFragment on EncounterParticipants {
    __typename
    type
    created_at
  }
`;

export const ENCOUNTER_ADMISSIONS_CORE_FRAGMENT = gql`
  fragment EncounterAdmissionsCoreFragment on EncounterAdmissions {
    __typename
    admit_source
    discharge_disposition
    readmission
    created_at
  }
`;

/*
 ════════════════════════════════════════
 =========== CART/WISHLIST ===========
 ════════════════════════════════════════
 */

export const WEB_CART_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment WebCartCoreFragment on WebCarts {
    id
    cart_holder
    expiresAt
  }
`);

export const WEB_CART_ITEM_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment WebCartItemCoreFragment on WebCartItems {
    id
    cart_id
    productCode
    name
    price
    tax
    thumbnail {
      url
    }
  }
`);

export const WEB_CART_SUMMARY_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment WebCartSummaryCoreFragment on WebCartSummaries {
    cart_id
    total
    subtotal
    tax
    surcharge
    item_count
  }
`);
export const WEB_CART_BOOKING_REQUEST_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment WebCartBookingRequestCoreFragment on WebCartBookingRequests {
    id
    cart_id
    items {
      id
      price
      name
    }
    location {
      location_id
      alias
      address {
        address_id
        formatted_address
      }
    }
    slot {
      slot_id
      start
      end
    }
  }
`);

export const CartItemsFragment = graphql(/* GraphQL */`
  fragment CartItemsFragment on WebCarts {
    id
    items {
      ...WebCartItemCoreFragment
    }
  }
`);

/*
 ════════════════════════════════
 =========== TIMELINE ===========
 ════════════════════════════════
 */

export const UserTimelineFragment = graphql(/* GraphQL */ `
  fragment UserTimelineFragment on TimelineEntry {
    __typename
    id
    title
    message
    action
    timestamp
    subject {
      __typename
      id
      type
      name
    }
    reference {
      __typename
      id
      resource
    }
    extras {
      __typename
      category
      link
      meta
    }
  }
`);

export const FeedItemFragment = graphql(/* GraphQL */`
  fragment FeedItemFragment on FeedItemObject {
    __typename
    id
    elementType
    publishDate
    relativePublishedDate
  }
`);

export const FeedActorFragment = graphql(/* GraphQL */`
  fragment FeedActorObjectFragment on FeedActorObject {
    __typename
    id
    type
    name
    url
  }
`);

export const FeedElementCollectionFragment = graphql(/* GraphQL */`
  fragment FeedElementCollectionFragment on FeedElementCollectionObject {
    __typename
    count
    currentPage
    totalPages
  }
`);

export const LinkCapabilityFragment = graphql(/* GraphQL */`
  fragment LinkCapabilityObjectFragment on LinkCapabilityObject {
    __typename
    url
    urlName
  }
`);

export const FeedItemCapabilityFragment = graphql(/* GraphQL */`
  fragment FeedItemCapabilityFragment on FeedElementCapabilitiesObject {
    __typename
  }
`);

export const FeedBundleCapabilityFragment = graphql(/* GraphQL */`
  fragment FeedBundleCapabilityFragment on FeedBundleCapabilityObject {
    __typename
    bundleType
    totalElements
  }
`);

export const TrackedChangeBundleCapabilityFragment = graphql(/* GraphQL */`
  fragment TrackedChangeBundleCapabilityFragment on TrackedChangeBundleCapabilityObject {
    __typename
    bundleType
    totalElements
  }
`);

export const TrackedChangeItemFragment = graphql(/* GraphQL */`
  fragment TrackedChangeItemFragment on TrackedChangeItemObject {
    __typename
    fieldName
    newValue
    oldValue
    summary
  }
`);

export const MessageBodyObjectFragment = graphql(/* GraphQL */`
  fragment MessageBodyObjectFragment on MessageBodyObject {
    __typename
    text
    isRichText
  }
`);

export const MessageBodySegmentFragment = graphql(/* GraphQL */`
  fragment MessageBodySegmentFragment on MessageBodyObject {
    messageSegments {
      ... on MessageSegmentTextObject {
        ...MessageSegmentTextFragment
      }
      ... on MessageSegmentLinkObject {
        __typename
        text
        type
        linkUrl: url
      }
      ... on MessageSegmentMarkupBeginObject {
        ...MessageSegmentMarkupBeginFragment
      }
      ... on MessageSegmentMarkupEndObject {
        ...MessageSegmentMarkupEndFragment
      }
    }
  }
`);

export const MessageSegmentTextFragment = graphql(/* GraphQL */`
  fragment MessageSegmentTextFragment on MessageSegmentTextObject {
    __typename
    text
    type
  }
`);

export const MessageSegmentLinkFragment = graphql(/* GraphQL */`
  fragment MessageSegmentLinkFragment on MessageSegmentLinkObject {
    __typename
    text
    type
    url
  }
`);

export const MessageSegmentMarkupBeginFragment = graphql(/* GraphQL */`
  fragment MessageSegmentMarkupBeginFragment on MessageSegmentMarkupBeginObject {
    __typename
    text
    type
    altText
    htmlTag
    markupType
    url
  }
`);

export const MessageSegmentMarkupEndFragment = graphql(/* GraphQL */`
  fragment MessageSegmentMarkupEndFragment on MessageSegmentMarkupEndObject {
    __typename
    text
    type
    htmlTag
    markupType
  }
`);

/*
 ═════════════════════════════════════════
 =========== PAYMENT ===========
 ═════════════════════════════════════════
 */

export const PAYMENT_PROFILE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PaymentProfileCoreFragment on PaymentProfiles {
    __typename
    profile_id
    user {
      user_id
    }
    authnet_payment_profile_id
    first_name
    last_name
    card_type
    last_four_of_card
    billing_address
    state
    city
    zip
    expiration
    is_default
    is_active
  }
`);

/*
 ═════════════════════════════════════════
 =========== ADDRESSES ===========
 ═════════════════════════════════════════
 */

export const ADDRESS_CORE_FRAGMENT = gql`
  fragment AddressCoreFragment on WebCynergyAddresses {
    __typename
    address_id
    city
    state
    zip
    address_line_1
    address_line_2
    nickname
    formatted_address
    type
    longitude
    latitude
    is_default
    place_id
  }
`;

/*
 ═════════════════════════════════════════
 =========== CONTACTS ===========
 ═════════════════════════════════════════
 */
export const CONTACT_POINT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ContactPointCoreFragment on ContactPoints {
    __typename
    contact_point_id
    system
    use
    value
    rank
    created_at
  }
`);

export const CONTACT_DETAILS_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ContactDetailsCoreFragment on ContactDetails {
    __typename
    contact_detail_id
    name
    purpose
  }
`);

export const CONTACT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ContactCoreFragment on Contacts {
    __typename
    contact_id
    first_name
    last_name
    nickname
    email
    phone
    authorized_access
    contact_type
    contact_relation_type
  }
`);

export const CONTACT_INFORMATION_FRAGMENT = graphql(/* GraphQL */`
  fragment ContactInformationFragment on ContactDetails {
    ...ContactDetailsCoreFragment
    telecom {
      ...ContactPointCoreFragment
    }
  }
`);


/*
 ═════════════════════════════════════════
 =========== ORGANIZATIONS ===========
 ═════════════════════════════════════════
 */

export const ORGANIZATIONS_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment OrganizationsCoreFragment on Organizations {
    __typename
    organization_id
    organization_role
    organization_type
    safety_precautions
    alias
    name
    preferred_member
  }
`);

/*
 ═════════════════════════════════════════
 =========== LOCATIONS ===========
 ═════════════════════════════════════════
 */

export const LOCATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment LocationsCoreFragment on Locations {
    location_id
    alias
    name
    description
    phone
    preferred_member
    organization_id
    todays_operating_hours
    open_now
    meta_data {
      priority
      rating
    }
    location_role
    location_status

  }
`);

export const LOCATION_OPERATING_HOURS_FRAGMENT = graphql(/* GraphQL */`
  fragment LocationOperatingHoursFragment on Locations {
    preferred_timeslots {
      weekday_text
      periods {
        open {
          day
          time
        }
        close {
          day
          time
        }
      }
    }
  }
`);

/*
 ═════════════════════════════════════════
 =========== APPOINTMENTS ===========
 ═════════════════════════════════════════
 */
export const APPOINTMENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment AppointmentCoreFragment on Appointments {
    __typename
    appointment_id
    patient_id
    title
    patient_name
    subject
    type
    category
    scheduled_start
    scheduled_end
    status
    description
  }
`);

export const APPOINTMENT_SERVICE_TYPES_FRAGMENT = graphql(/* GraphQL */`
  fragment AppointmentServiceTypesFragment on AppointmentServiceTypes {
    __typename
    service_id
    type_id
    name
  }
`);

/*
 ═════════════════════════════════════
 =========== ECOMMERCE ===========
 ═════════════════════════════════════
 */

export const WEBSTORE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment WebStoreCoreFragment on WebStores {
    __typename
    name
    type
    code
    isLocal @client
  }
`);

export const PRODUCT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ProductCoreFragment on Products {
    __typename
    code
    name
    product_id
    description
    family
    isInCart @client
    isWishListed @client
  }
`);

export const PRODUCT_CATEGORIES_CORE_FRAGMENT = graphql(/* GraphQL */ `
  fragment ProductCategoriesCoreFragment on ProductCategories {
    __typename
    category_id
    label
    description
    number_of_products
  }
`);

export const PRODUCT_CATALOG_ITEMS_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ProductCatalogItemsCoreFragment on ProductCatalogItems {
    __typename
    item_id
  }
`);

export const PRODUCT_CATALOG_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ProductCatalogCoreFragment on ProductCatalogs {
    __typename
    catalog_id
    name
    description
    code
    number_of_categories
    number_of_products
  }
`);

export const PRICE_BOOK_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PriceBookCoreFragment on PriceBooks {
    __typename
    book_id
    name
    code
    description
  }
`);

export const PRICE_BOOK_ENTRY_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment PriceBookEntryCoreFragment on PriceBookEntries {
    __typename
    entry_id
    name
    product_code
    unit_price
  }
`);

export const STORE_CATEGORY_DEFINITION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment WebStoreCategoryDefinitionCoreFragment on WebStoreCategoryDefinitions {
    __typename
    definition_id
    type
    label
    description
    image {
      attachment_id
      title
      size
      width
      height
      url
    }
  }
`);


export const USER_PRODUCT_BUNDLE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment UserProductBundleCoreFragment on UserProductBundles {
    __typename
    bundle_id
    account_id
    name
    owner
    is_shared
    description
    created_at
  }
`);


export const USER_PRODUCT_BUNDLE_ITEM_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment UserProductBundleItemCoreFragment on UserProductBundleItems {
    __typename
    bundle_item_id
    name
    sequence
    product_id
    pricebook_entry_id
    store_id
  }
`);

/*
 ═════════════════════════════════════════
 =========== SERVICES ===========
 ═════════════════════════════════════════
 */

export const HEALTHCARE_SERVICE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment HealthcareServiceCoreFragment on HealthcareServices {
    __typename
    service_id
    name
    type
    category
    active
    speciality
  }
`);

export const HEALTHCARE_SERVICE_LOCATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment HealthcareServiceLocationCoreFragment on HealthcareServiceLocations {
    __typename
    service_location_id
    distance
  }
`);

export const DIAGNOSTIC_SERVICE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment DiagnosticServiceCoreFragment on DiagnosticServices {
    service_id
    title
    subtitle
    type
  }
`);

export const DIAGNOSTIC_SERVICE_BIOMARKERS_FRAGMENT = graphql(/* GraphQL */`
  fragment DiagnosticServiceBiomarkersFragment on DiagnosticServices {
    service_id
    biomarkers {
      ...ObservationDefinitionCoreFragment
      members {
        ...ObservationDefinitionCoreFragment
      }
      components {
        ...ObservationDefinitionComponentCoreFragment
      }
    }
  }
`);

/*
 ════════════════════════════════════
 =========== CRITERIA ===========
 ════════════════════════════════════
 */

export const CRITERIA_CORE_FRAGMENT = gql`
  fragment CriteriaCoreFragment on Criteria {
    __typename
    criteria_number
    title
    publisher
    reference_url
    citations
  }
`;

export const CRITERIA_DEFINITION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CriteriaDefinitionCoreFragment on CriteriaDefinitions {
    __typename
    definition_id
    slug
    title
    description
    publisher
    url
    created_by
    created_at
  }
`);

export const CRITERIA_ITEM_CORE_FRAGMENT = gql`
  fragment CriteriaItemCoreFragment on CriteriaItems {
    __typename
    item_id
    link_id
    syndrome
    icd10 {
      __typename
      icd10_code
      label
      description
      hex
    }
  }
`;

export const CRITERIA_RULE_CORE_FRAGMENT = gql`
  fragment CriteriaRuleCoreFragment on CriteriaRules {
    __typename
    rule_id
    statement
    required_behavior
  }
`;

export const CRITERIA_FORM_RESULT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CriteriaFormResultCoreFragment on CriteriaFormResults {
    __typename
    result_id
    passed
    created_at
  }
`);

export const CRITERIA_FORM_RESULT_FLAG_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CriteriaFormResultFlagCoreFragment on CriteriaFormResultFlags {
    __typename
    flag_id
    syndrome
    created_at
  }
`);

export const CRITERIA_FORM_RESULT_FLAG_ITEM_CORE_FRAGMENT = graphql(/* GraphQL */ `
  fragment CriteriaFormResultFlagItemCoreFragment on CriteriaFormResultFlagItems {
    __typename
    label
    icd10_codes
    applies_to
    created_at
  }
`);

/*
 ═════════════════════════════════════════
 =========== CLINICAL CARE ===========
 ═════════════════════════════════════════
 */

export const CARE_PLAN_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CarePlanCoreFragment on CarePlans {
    __typename
    care_plan_id
    title
    description
    status
    category
    created_at
  }
`);

export const CARE_TEAM_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CareTeamCoreFragment on CareTeams {
    __typename
    care_team_id
    name
    status
    category
    created_at
  }
`);

export const CARE_TEAM_MEMBER_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CareTeamMemberCoreFragment on CareTeamMembers {
    __typename
    member_id
    role
    name
    created_at
  }
`);

export const DETECTED_ISSUE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment DetectedIssueCoreFragment on DetectedIssues {
    __typename
    status
    category
    severity
    detail
    identified_at
    mitigation_action
    mitigation_date
    reference_uri
  }
`);

export const CONDITIONS_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ConditionsCoreFragment on Conditions {
    __typename
    condition_id
    category
    body_site
    verification_status
    clinical_status
    severity
    note
    recorded_date
    abatement_date_string
    abatement_date_time
    onset_date_string
    onset_date_time
  }
`);

export const CONDITION_ASSESSMENT_CORE_FRAGMENT = graphql(`
  fragment ConditionAssessmentCoreFragment on ClinicalImpressions {
    __typename
    impression_id
    status
    description
    summary
    change_pattern
    numeric_rating
    protocol
    assessment_date_time
    status_reason
    created_at
  }
`);

export const CLINICAL_NOTES_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ClinicalNoteCoreFragment on ClinicalNotes {
    __typename
    note_id
    subject
    text
    created_at
    authored
  }
`);

/*
 ═════════════════════════════════════════
 =========== MEDICATIONS ===========
 ═════════════════════════════════════════
 */

export const MEDICATION_CORE_FRAGMENT = gql`
  fragment MedicationCoreFragment on Medications {
    __typename
    medication_id
    brand_name
    status
    code
    dose_form
    manufacturer
  }
`;

export const MEDICATION_USAGES_CORE_FRAGMENT = gql`
  fragment MedicationUsageCoreFragment on MedicationUsages {
    __typename
    usage_id
    practitioner_id
    usage_status: status
    notes
    adherence
    date_asserted
    category
    dosage_instruction
    administration_method
    administration_timing
    timing_display
    effective
    days_left
    effective_period {
      __typename
      period_id
      start
      end
    }
  }
`;

export const MEDICATION_CORE_FRAGMENT_ALT = graphql(/* GraphQL */`
  fragment MedicationCoreFragmentAlt on Medications {
    __typename
    medication_id
    brand_name
    status
    code
    dose_form
    manufacturer
  }
`);

export const MEDICATION_USAGE_CORE_FRAGMENT_ALT = graphql(/* GraphQL */`
  fragment MedicationUsageCoreFragmentAlt on MedicationUsages {
    __typename
    usage_id
    practitioner_id
    usage_status: status
    notes
    adherence
    date_asserted
    category
    dosage_instruction
    administration_method
    administration_timing
    timing_display
    effective
    days_left
    effective_period {
      ...PeriodCoreFragment
    }
  }
`);

/*
 ═════════════════════════════════════════
 =========== DIAGNOSTICS ===========
 ═════════════════════════════════════════
 */

export const DIAGNOSTIC_REPORT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment DiagnosticReportCoreFragment on DiagnosticReports {
    __typename
    report_id
    code
    category
    status
    conclusion_code
    issued
  }
`);

export const OBSERVATION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ObservationCoreFragment on Observations {
    __typename
    observation_id
    code
    category
    status
    method
    target_type
    interpretation
    created_at
  }
`);

export const OBSERVATION_FLAG_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ObservationFlagCoreFragment on ObservationFlags {
    __typename
    flag_id
    hl7_code
    display
    alert_priority
    created_at
  }
`);

export const OBSERVATION_DEFINITION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ObservationDefinitionCoreFragment on ObservationDefinitions {
    definition_id
    title
    type
    method
    category
  }
`);

export const OBSERVATION_DEFINITION_COMPONENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ObservationDefinitionComponentCoreFragment on ObservationDefinitionComponents {
    component_id
    name
  }
`);

export const SPECIMEN_DEFINITION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment SpecimenDefinitionCoreFragment on SpecimenDefinitions {
    definition_id
  }
`);

export const SPECIMEN_COLLECTION_DEFINITION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment SpecimenDefinitionCollectionCoreFragment on SpecimenDefinitionCollections {
    collection_id
    patient_preparation
    method {
      text
    }
    specimen_type
  }
`);

/*
 ══════════════════════════════
 ========= LOINC =========
 ═══════════════════════════════
 */
export const LOINC_CODE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment LoincCodeCoreFragment on LoincCodes {
    __typename
    code
    display
    status
    classification
    class_type
    order_observation
  }
`);

/*
 ══════════════════════════════════════
 =========== GENOTYPES ===========
 ══════════════════════════════════════
 */

export const GENOTYPE_CORE_FRAGMENT = graphql(/*GraphQL*/`
  fragment GenotypeCoreFragment on Genotypes {
    __typename
    genotype_id
    gene
    encoding
    mechanism
    classification
  }
`);

/*
 ══════════════════════════════
 ========= AGENTS =========
 ═══════════════════════════════
 */

export const AGENT_CORE_FRAGMENT = graphql(/*GraphQL*/`
  fragment AgentCoreFragment on Agents {
    __typename
    agent_id
    name
  }
`);

export const AGENT_CLASS_CLASSIFICATION_CORE_FRAGMENT = graphql(/*GraphQL*/`
  fragment AgentClassificationCoreFragment on AgentClassifications {
    __typename
    classification_number
    code
    label
    description
  }
`);

/*
 ═════════════════════════════════════════
 =========== ORDERS/REQUESTS ===========
 ═════════════════════════════════════════
 */

export const ORDERS_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment OrderCoreFragment on Orders {
    __typename
    order_id
    title
    description
    status
    status_reason
    priority
    recurs
    series_id
    event_id
    is_series_source
    scheduled_date
    fulfilled_at
    requested_collection_date
    is_reviewed
    created_by
    deleted_at
  }
`);

export const ORDER_ITEM_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment OrderLineItemCoreFragment on OrderLineItems {
    __typename
    line_item_id
    description
    net_total
    tax
  }
`);

export const ORDER_AGENTS_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment OrderAgentCoreFragment on OrderAgents {
    __typename
    agent_id
    name
    active
    role
    comment
  }
`);

export const ORDER_AGENT_ACTOR_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment OrderAgentActorCoreFragment on OrderAgentActor {
    __typename
    ... on Practitioners {
      __typename
      practitioner_id
    }
    ... on Organizations {
      __typename
      organization_id
    }
    ... on Locations {
      __typename
      location_id
    }
    ... on CareTeams {
      __typename
      care_team_id
    }
  }
`);

export const SERVICE_REQUEST_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment ServiceRequestCoreFragment on ServiceRequests {
    __typename
    request_id
    code
    status
    priority
    intent
    category
    occurrence
    created_at
  }
`);

/*
 ═══════════════════════════════════════════════════
 =========== FILES/ATTACHMENTS/DOCUMENTS ===========
 ═══════════════════════════════════════════════════
 */

export const USER_FILES_FRAGMENT = gql`
  fragment FileCoreFragment on BucketItem {
    __typename
    name
    etag
    lastModified
    prefix
    size
  }
`;

export const USER_FILE_METADATA_FRAGMENT = gql`
  fragment FileMetaDataFragment on BucketMetaData {
    __typename
    Fileextension
    Filename
    Folder
    Isstarred
    Istrashed
    Label
    Lastaccessed
  }
`;

export const BUCKET_FRAGMENT = gql`
  fragment FileBucketFragment on Bucket {
    __typename
    folder
    files {
      __typename
      size
      name
      prefix
      lastModified
      etag

    }
  }
`;

export const ATTACHMENT_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment AttachmentCoreFragment on Attachments {
    __typename
    attachment_id
    content_type
    title
    url
    width
    height
    pages
    size
    created_at
  }
`);

export const DOCUMENT_REFERENCE_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment DocumentReferenceCoreFragment on DocumentReferences {
    __typename
    reference_id
    status
    description
    content_type
    document_status
    security_tag
    version
  }
`);

/*
 ════════════════════════════════════
 =========== PREFERENCES ===========
 ════════════════════════════════════
 */

export const USER_COLLECTION_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment UserCollectionCoreFragment on Collections {
    __typename
    collection_id
    name
    color
    url
    is_default
  }
`);

export const COLLECTION_ENTRY_CORE_FRAGMENT = graphql(/* GraphQL */`
  fragment CollectionEntryCoreFragment on CollectionEntries {
    __typename
    entry_id
    name
    url
  }
`);

/*
 ═════════════════════════════════════════
 =========== API ===========
 ═════════════════════════════════════════
 */

export const GOOGLE_GEOCODE_CORE_FRAGMENT = gql`
  fragment GoogleGeocodeCoreFragment on GoogleGeocodingResponseType {
    __typename
    results {
      address_components {
        long_name
        short_name
        types
      }
      formatted_address
      types
      geometry {
        location {
          lat
          lng
        }
      }
    }
    status
    error_message
  }
`;

export const GOOGLE_GEOCODE_COORDINATE_FRAGMENT = gql`
  fragment GoogleGeocodingCoordinateFragments on GoogleGeocodingResponseType {
    results {
      geometry {
        location {
          lat
          lng
        }
      }
    }
  }
`;


/*
 ═════════════════════════════════════
 =========== DATA QUALITY ===========
 ═════════════════════════════════════
 */

// export const MATCH_RESULT_OBJECT_FRAGMENT = gql(/* GraphQL */`
//   fragment MatchResultObjectCoreFragment on MatchResultObject {
//     __typename
//   }
// `)
